import { call, put, select, takeLatest } from 'redux-saga/effects';
import moment from 'moment';

import {
  GET_QUESTIONS_SECRETS,
  SUBMIT_PASSWORD_RECOVERY_STEP,
  SUBMIT_LOGIN_RECOVERY_STEP,
  CHANGE_PASSWORD,
  CHANGE_SECRET_QUESTION,
  ENABLE_NOTIFICATIONS,
} from 'store/credentials/constants';
import {
  setLoginRecoveryStep,
  setPasswordRecoveryStep,
  setQuestionsSecret,
  submitPasswordRecoveryStepFail,
  submitPasswordRecoveryStepSuccess,
  submitLoginRecoveryStepFail,
  setToken,
  changePasswordSuccess,
  changePasswordFailed,
  changeSecretQuestionSuccess,
  changeSecretQuestionFailed,
  enableNotificationsSuccess,
  enableNotificationsFail,
} from 'store/credentials/actions';
import {
  forgotPasswordByEmailApi,
  getQuestionSecretApi,
  getRecoveryMethodsPasswordApi,
  verifyPinMethodForgotPasswordApi,
  verifyCinBdApi,
  forgotPwdByQuestionApi,
  sendMailForgotLoginApi,
  updatePasswordApi,
  changePasswordApi,
  changeSecretQuestionApi,
  enableNotificationsApi,
} from 'store/credentials/apis';
import { pushTo } from 'helpers/routes';

import { getLocaleSelector } from 'store/LanguageProvider/selectors';
import {
  getPasswordCurrentRecoveryStepNameSelector,
  getLoginCurrentRecoveryStepNameSelector,
  getTokenSelector,
} from 'store/credentials/selectors';
import {
  CHOOSE_METHODS_RECOVERY_PASS,
  FORGOT_PASSWORD_LOGIN,
  FORGOT_PASSWORD_PIN_EMAIL,
  INSERT_NEW_PASSWORD,
  FORGOT_MDP_QUESTION_SCRT,
} from 'containers/PasswordRecoveryPages/constants';

import {
  INSERT_INFOS,
  SEND_LOGIN_EMAIL,
} from 'containers/LoginRecoveryPage/constants';
import { setUserInfo } from 'store/user/actions';
import PAGES from 'routes/pages';
import { setLogin } from 'store/user/actions';
import { getLoginSelector } from 'store/user/selectors';
import { setToasterOpen } from 'store/global/actions';
import { get, isEmpty } from 'lodash';
import {
  ChangePasswordGeneratorType,
  ChangeSecretQuestionGeneratorType,
  EnableNotificationsGeneratorType,
} from './types';

export function* getQuestionSecret() {
  try {
    const language = yield select(getLocaleSelector);
    const response = yield call(getQuestionSecretApi, {
      language,
    });
    if (response.data.success) {
      const data = response.data.data.map((question: any) => ({
        label: question.question,
        value: String(question.id_question),
      }));
      yield put(setQuestionsSecret(data));
    }
  } catch (error) {
    yield put(setQuestionsSecret(''));
  }
}
export function* watchGetQuestionSecret() {
  yield takeLatest(GET_QUESTIONS_SECRETS, getQuestionSecret);
}

export function* submitRecoverySignupStep({
  payload,
}: {
  type: typeof SUBMIT_PASSWORD_RECOVERY_STEP;
  payload: any;
}) {
  const currentRecoveryStepName = yield select(
    getPasswordCurrentRecoveryStepNameSelector,
  );

  switch (currentRecoveryStepName) {
    case FORGOT_PASSWORD_LOGIN:
      yield call(recoveryMethodsPassword, payload);
      break;
    case CHOOSE_METHODS_RECOVERY_PASS:
      if (payload.isEmailState) {
        yield call(forgotPasswordByEmail);
        break;
      } else if (payload.isQuestionState) {
        yield call(forgotPasswordByQuestionSecret);
        break;
      }
      break;
    case FORGOT_PASSWORD_PIN_EMAIL:
      yield call(verifyPinMethodForgotPassword, payload);
      break;
    case FORGOT_MDP_QUESTION_SCRT:
      yield call(forgotPwdByQuestion, payload);
      break;
    case INSERT_NEW_PASSWORD:
      yield call(updatePassword, payload);
      break;
    default:
      yield call(recoveryMethodsPassword, payload);
      break;
  }
}

export function* verifyPinMethodForgotPassword(pin: string) {
  try {
    const token = yield select(getTokenSelector);
    const { data: response } = yield call(verifyPinMethodForgotPasswordApi, {
      token,
      pin,
    });
    if (response.success) {
      const { token } = response.data;
      yield put(setToken(token));
      yield put(setPasswordRecoveryStep(INSERT_NEW_PASSWORD));
    } else {
      yield put(submitPasswordRecoveryStepFail(response.message));
    }
  } catch (error) {
    yield put(submitPasswordRecoveryStepFail(error));
  }
}
export function* submitLoginRecoveryStep({
  payload,
}: {
  type: typeof SUBMIT_LOGIN_RECOVERY_STEP;
  payload: any;
}) {
  const currentLoginRecoveryStepName = yield select(
    getLoginCurrentRecoveryStepNameSelector,
  );

  switch (currentLoginRecoveryStepName) {
    case INSERT_INFOS:
      yield call(verifyCinBd, payload);
      break;
    case SEND_LOGIN_EMAIL:
      yield call(sendMailForgotLogin, payload);
      break;
    default:
      yield call(verifyCinBd, payload);
      break;
  }
}

export function* verifyCinBd(data: { bday: string; cin: string }) {
  try {
    const dob = moment(data.bday).format('DD/MM/YYYY');

    const response = yield call(verifyCinBdApi, {
      dob,
      cin: data.cin,
    });

    if (response.data.success) {
      yield put(setToken(response.data.data.token));
      yield put(
        setUserInfo({
          email: response.data.data.email,
          login: response.data.data.login.toString(),
          firstName: response.data.data.firstName,
          lastName: response.data.data.lastName,
        }),
      );
      yield put(setLoginRecoveryStep(SEND_LOGIN_EMAIL));
    }
  } catch (error) {
    yield put(submitLoginRecoveryStepFail(error));
  }
}

export function* sendMailForgotLogin(data: { email: string; login: string }) {
  try {
    const token = yield select(getTokenSelector);
    const language = yield select(getLocaleSelector);
    const response = yield call(sendMailForgotLoginApi, {
      language: language,
      token: token,
      email: data.email,
      login: data.login,
    });
    if (response.data.success) {
      yield call(pushTo, PAGES.LOGIN);
    }
  } catch (error) {
    yield put(submitLoginRecoveryStepFail(error));
  }
}
export function* recoveryMethodsPassword(payload: { login: string }) {
  try {
    yield put(setLogin(payload.login));
    const { data: response } = yield call(
      getRecoveryMethodsPasswordApi,
      payload.login,
    );
    if (response.success && !isEmpty(response.data.email)) {
      const {
        email,
        token,
        is_email: isEmail,
        is_question: isQuestion,
      } = response.data;
      yield put(setToken(token));
      yield put(
        submitPasswordRecoveryStepSuccess({
          isEmail,
          isQuestion,
          hashedEmail: email,
        }),
      );
      yield put(setPasswordRecoveryStep(CHOOSE_METHODS_RECOVERY_PASS));
    } else {
      yield put(submitPasswordRecoveryStepFail('loginError'));
    }
  } catch (error) {
    yield put(submitPasswordRecoveryStepFail(error));
  }
}

export function* forgotPasswordByEmail() {
  try {
    const token = yield select(getTokenSelector);
    const language = yield select(getLocaleSelector);
    const { data: response } = yield call(forgotPasswordByEmailApi, {
      token,
      language,
    });
    if (response.success) {
      const { token } = response.data;
      yield put(setToken(token));
      yield put(setPasswordRecoveryStep(FORGOT_PASSWORD_PIN_EMAIL));
    } else {
      yield put(submitPasswordRecoveryStepFail(response.message));
    }
  } catch (error) {
    yield put(submitPasswordRecoveryStepFail(error));
  }
}
export function* forgotPasswordByQuestionSecret() {
  try {
    const language = yield select(getLocaleSelector);
    const response = yield call(getQuestionSecretApi, {
      language,
    });
    if (response.data.success) {
      const data = response.data.data.map((question: any) => ({
        label: question.question,
        value: String(question.id_question),
      }));
      yield put(setQuestionsSecret(data));
      yield put(setPasswordRecoveryStep(FORGOT_MDP_QUESTION_SCRT));
    }
  } catch (error) {
    yield put(submitPasswordRecoveryStepFail(error));
  }
}
export function* forgotPwdByQuestion(payload: {
  questionSecret: string;
  response: string;
}) {
  try {
    const token = yield select(getTokenSelector);
    const { data: response } = yield call(forgotPwdByQuestionApi, {
      token: token,
      id_question: payload.questionSecret,
      response_question: payload.response,
    });
    if (response.success) {
      const { token } = response.data;
      yield put(setToken(token));
      yield put(setPasswordRecoveryStep(INSERT_NEW_PASSWORD));
    }
  } catch (error) {
    yield put(submitPasswordRecoveryStepFail(error));
  }
}

export function* updatePassword(payload: {
  newPassword: string;
  confirmPassword: string;
}) {
  try {
    const login = yield select(getLoginSelector);
    const token = yield select(getTokenSelector);
    const { data: response } = yield call(updatePasswordApi, {
      login,
      token,
      newPassword: payload.newPassword,
      confirmPassword: payload.confirmPassword,
    });
    if (response.success) {
      yield call(pushTo, '/');
      yield put(
        setToasterOpen({
          title: 'passwordSuccessfullyChanged',
          severity: 'success',
        }),
      );
    } else {
      yield put(submitPasswordRecoveryStepFail(response.message));
    }
  } catch (error) {
    yield put(submitPasswordRecoveryStepFail(error));
  }
}

export function* watchSubmitSignupStep() {
  yield takeLatest(SUBMIT_PASSWORD_RECOVERY_STEP, submitRecoverySignupStep);
}
export function* watchSubmitLoginRecoveryStep() {
  yield takeLatest(SUBMIT_LOGIN_RECOVERY_STEP, submitLoginRecoveryStep);
}

export function* changePassword({ payload }: ChangePasswordGeneratorType) {
  try {
    const token = localStorage.getItem('token');
    const { data } = yield call(changePasswordApi, { ...payload, token });
    if (get(data, 'success')) {
      yield put(
        setToasterOpen({
          title: 'passwordSuccessfullyChanged',
          severity: 'success',
        }),
      );
    }
    yield put(changePasswordSuccess());
  } catch (err) {
    if (
      !get(err, 'response.data.success') &&
      get(err, 'response.data.code_error') === 2000
    ) {
      yield put(
        setToasterOpen({
          title: 'currentPasswordIncorrect',
          severity: 'error',
        }),
      );
    } else {
      yield put(
        setToasterOpen({
          title: 'defaultErrorMessage',
          severity: 'error',
        }),
      );
    }
    yield put(changePasswordFailed());
  }
}

export function* watchChangePassword() {
  yield takeLatest(CHANGE_PASSWORD, changePassword);
}

export function* changeSecretQuestion({
  payload,
}: ChangeSecretQuestionGeneratorType) {
  try {
    const token = localStorage.getItem('token');
    const { data } = yield call(changeSecretQuestionApi, { ...payload, token });
    if (get(data, 'success')) {
      yield put(
        setToasterOpen({
          title: 'secretQuestionSuccessfullyChanged',
          severity: 'success',
        }),
      );
    }
    yield put(changeSecretQuestionSuccess());
  } catch (err) {
    if (
      !get(err, 'response.data.success') &&
      get(err, 'response.data.code_error') === 2000
    ) {
      yield put(
        setToasterOpen({
          title: 'currentPasswordIncorrect',
          severity: 'error',
        }),
      );
    } else {
      yield put(
        setToasterOpen({
          title: 'defaultErrorMessage',
          severity: 'error',
        }),
      );
    }
    yield put(changeSecretQuestionFailed());
  }
}

export function* watchChangeSecretQuestion() {
  yield takeLatest(CHANGE_SECRET_QUESTION, changeSecretQuestion);
}

export function* enableNotifications({
  payload,
}: EnableNotificationsGeneratorType) {
  try {
    const token = localStorage.getItem('token');
    const login = yield select(getLoginSelector);
    const { data } = yield call(enableNotificationsApi, {
      ...payload,
      login,
      token,
    });
    if (get(data, 'success')) {
      yield put(
        setToasterOpen({
          title: 'actionCompletedSuccessfully',
          severity: 'success',
        }),
      );
    }
    yield put(enableNotificationsSuccess());
  } catch (err) {
    yield put(
      setToasterOpen({
        title: 'defaultErrorMessage',
        severity: 'error',
      }),
    );
    yield put(enableNotificationsFail());
  }
}

export function* watchEnableNotifications() {
  yield takeLatest(ENABLE_NOTIFICATIONS, enableNotifications);
}

export default [
  watchGetQuestionSecret,
  watchSubmitSignupStep,
  watchSubmitLoginRecoveryStep,
  watchChangePassword,
  watchChangeSecretQuestion,
  watchEnableNotifications,
];
